/**
 * Hook para gerenciar módulos no sistema RPG-IA
 *
 * Fornece funcionalidades para:
 * - Listar módulos disponíveis
 * - Listar módulos ativados em uma mesa
 * - Ativar/desativar módulos
 */

import { useState, useCallback } from 'react'
import { apiService } from '@/services/api'
import type {
  ModuleManifest,
  ModulesListResponse,
  TableModulesListResponse,
  TableModuleInfo
} from '@/modules/types'
import { toast } from 'sonner'

export function useModules() {
  const [modules, setModules] = useState<ModuleManifest[]>([])
  const [tableModules, setTableModules] = useState<TableModuleInfo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Lista todos os módulos disponíveis no sistema
   */
  const listAvailableModules = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiService.request<ModulesListResponse>(
        '/modules',
        { method: 'GET' }
      )
      setModules(response.modules)
      return response.modules
    } catch (err) {
      console.error('Erro ao listar módulos:', err)
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar módulos disponíveis'
      setError(errorMessage)
      toast.error(errorMessage)
      return []
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Lista módulos ativados em uma mesa específica
   */
  const listTableModules = useCallback(async (tableId: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiService.request<TableModulesListResponse>(
        `/modules/table/${tableId}`,
        { method: 'GET' }
      )
      setTableModules(response.modules)
      return response.modules
    } catch (err) {
      console.error('Erro ao listar módulos da mesa:', err)
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar módulos da mesa'
      setError(errorMessage)
      toast.error(errorMessage)
      setTableModules([])
      return []
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Ativa um módulo em uma mesa
   */
  const activateModule = useCallback(
    async (tableId: string, moduleId: string, config?: Record<string, unknown>) => {
      setIsLoading(true)
      try {
        const response = await apiService.request(
          `/modules/table/${tableId}/activate/${moduleId}`,
          {
            method: 'POST',
            body: JSON.stringify(config || {}),
          }
        )
        toast.success('Módulo ativado com sucesso!')
        return response
      } catch (error: any) {
        console.error('Erro ao ativar módulo:', error)
        const errorMessage = error?.message || 'Erro ao ativar módulo'
        toast.error(errorMessage)
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  /**
   * Desativa um módulo em uma mesa
   */
  const deactivateModule = useCallback(
    async (tableId: string, moduleId: string) => {
      setIsLoading(true)
      try {
        const response = await apiService.request(
          `/modules/table/${tableId}/deactivate/${moduleId}`,
          {
            method: 'POST',
          }
        )
        toast.success('Módulo desativado com sucesso!')
        return response
      } catch (error: any) {
        console.error('Erro ao desativar módulo:', error)
        const errorMessage = error?.message || 'Erro ao desativar módulo'
        toast.error(errorMessage)
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  return {
    modules,
    tableModules,
    isLoading,
    error,
    listAvailableModules,
    listTableModules,
    activateModule,
    deactivateModule,
  }
}
